/**
 * Pure prejoin derivations: device enumeration shaping, selection memory and the join payload.
 * Everything here is synchronous and side-effect free so the prejoin screen stays testable.
 */

import type {
  BackgroundMode,
  BackgroundQualitySetting,
  ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import type { FaceMaskId } from '../media/faceMasks';

/** Structural slice of MediaDeviceInfo the prejoin logic needs. */
export interface InputDeviceInfoLike {
  readonly deviceId: string;
  readonly kind: string;
  readonly label: string;
}

/** One selectable capture device with a user-facing label. */
export interface DeviceOption {
  readonly deviceId: string;
  readonly label: string;
}

export type InputDeviceKind = 'videoinput' | 'audioinput' | 'audiooutput';

/** Select value meaning "no device selected" (device missing or deliberately off). */
export const NO_DEVICE = '';

const GENERIC_LABEL: Readonly<Record<InputDeviceKind, string>> = {
  videoinput: 'Camera',
  audioinput: 'Microphone',
  audiooutput: 'Speaker',
};

/**
 * Shapes enumerated devices into selectable options for one kind. Entries without a deviceId
 * (browsers return those before permission is granted) are skipped; empty labels fall back to
 * a generic numbered name.
 */
export function deviceOptions(
  devices: readonly InputDeviceInfoLike[],
  kind: InputDeviceKind,
): DeviceOption[] {
  const generic = GENERIC_LABEL[kind];
  const options: DeviceOption[] = [];
  for (const device of devices) {
    if (device.kind !== kind || device.deviceId.length === 0) {
      continue;
    }
    options.push({
      deviceId: device.deviceId,
      label: device.label.length > 0 ? device.label : `${generic} ${options.length + 1}`,
    });
  }
  return options;
}

/**
 * Resolves the select value for a device kind: keeps the current selection when the device still
 * exists, falls back to the remembered preference, then to the first device, and to
 * {@link NO_DEVICE} when the kind has no devices at all. An explicit {@link NO_DEVICE} choice is
 * sticky: once the initial bootstrap resolution has run (`autoResolve` false), a NO_DEVICE
 * selection survives every re-enumeration - the user opted out of this kind, and a devicechange
 * pass must not resurrect it. Only the bootstrap pass (`autoResolve` true) auto-picks from an
 * empty selection.
 */
export function resolveSelection(
  options: readonly DeviceOption[],
  current: string,
  remembered: string | null,
  autoResolve: boolean,
): string {
  if (current === NO_DEVICE && !autoResolve) {
    return NO_DEVICE;
  }
  if (current !== NO_DEVICE && options.some((option) => option.deviceId === current)) {
    return current;
  }
  if (remembered !== null && options.some((option) => option.deviceId === remembered)) {
    return remembered;
  }
  const first = options[0];
  return first !== undefined ? first.deviceId : NO_DEVICE;
}

/** What the participant will publish on join. */
export type MediaMode = 'audio-video' | 'audio-only' | 'video-only' | 'no-media';

/** Derives the join mode from the selected device ids (empty means off). */
export function mediaMode(cameraDeviceId: string, micDeviceId: string): MediaMode {
  const hasCamera = cameraDeviceId !== NO_DEVICE;
  const hasMic = micDeviceId !== NO_DEVICE;
  if (hasCamera && hasMic) {
    return 'audio-video';
  }
  if (hasMic) {
    return 'audio-only';
  }
  if (hasCamera) {
    return 'video-only';
  }
  return 'no-media';
}

/** Payload the prejoin screen hands to the room page on join. */
export interface PrejoinJoinRequest {
  readonly displayName: string;
  readonly cameraDeviceId: string | null;
  readonly micDeviceId: string | null;
  /**
   * Present for an SSO join: the Keycloak access token from the tab's sign-in session. The
   * server derives identity and display name from it; the guest fields stay client-side only.
   */
  readonly ssoToken?: string | undefined;
  /** Selected speaker device id; applied to remote audio elements via setSinkId (best-effort). */
  readonly speakerDeviceId?: string | null | undefined;
  /** Mirror-video option: mirrors the local preview and the own in-room self-view. */
  readonly mirrorVideo?: boolean | undefined;
  /** Microphone processing switches chosen in the effects dialog; absent = browser default (on). */
  readonly micEchoCancellation?: boolean | undefined;
  readonly micNoiseSuppression?: boolean | undefined;
  readonly micAutoGainControl?: boolean | undefined;
  /** Chosen background treatment; persisted so the next prejoin preselects it. */
  readonly backgroundMode?: BackgroundMode | undefined;
  /** Chosen background quality setting; persisted so the next prejoin preselects it. */
  readonly backgroundQuality?: BackgroundQualitySetting | undefined;
  /** Chosen blur strength preset; persisted so the next prejoin preselects it. */
  readonly backgroundBlurStrength?: BlurStrength | undefined;
  /** Chosen whole-frame color filter; persisted so the next prejoin preselects it. */
  readonly backgroundVideoFilter?: VideoFilter | undefined;
  /** Chosen face-mask overlay; persisted so the next prejoin preselects it. */
  readonly backgroundMask?: FaceMaskId | undefined;
  /**
   * Quality level the "auto" benchmark resolved to during this prejoin (null when it has not
   * run or the setting is manual); persisted so the UI can show what auto picked next time.
   */
  readonly backgroundResolvedQuality?: ResolvedBackgroundQuality | null | undefined;
  /**
   * The processed camera handed over to the room: its track is published instead of a fresh
   * device capture. Ownership moves to the room (dispose releases the processor and its input
   * camera track); present whenever the composite pipeline runs - a background treatment or
   * a whole-frame color filter.
   */
  readonly background?: BackgroundHandover | undefined;
}

/** The running background processor handed from the prejoin to the room page on join. */
export interface BackgroundHandover {
  /** The background treatment of the handed-over pipeline (may be "none": filter-only). */
  readonly mode: BackgroundMode;
  /** The composited canvas video track (person sharp, background blurred/replaced). */
  readonly track: MediaStreamTrack;
  /**
   * The RAW camera track feeding the processor: the in-call effects machine rebuilds
   * future pipeline generations from it (a canvas output never feeds back in).
   */
  readonly cameraTrack: MediaStreamTrack;
  /** Releases the processor and its input camera track; the room calls it on every leave path. */
  readonly dispose: () => void;
}

/**
 * True when the join request starts the session with an active camera treatment (a
 * background mode, a whole-frame color filter or a face mask): the composite pipeline runs
 * from the first frame, so the effects-path rule of resolveSessionTier applies. The
 * handed-over processor (`background`) is the authoritative signal - the prejoin sets it
 * whenever the pipeline runs - and the persisted choice fields cover the degraded paths
 * where the processor could not start (the session still joins with the treatment on).
 */
export function sessionEffectsActive(request: PrejoinJoinRequest): boolean {
  return (
    request.background !== undefined ||
    (request.backgroundMode ?? 'none') !== 'none' ||
    (request.backgroundVideoFilter ?? 'none') !== 'none' ||
    (request.backgroundMask ?? 'none') !== 'none'
  );
}

/** Maps select values to the join payload: an empty selection becomes null (track not published). */
export function toJoinRequest(
  displayName: string,
  cameraDeviceId: string,
  micDeviceId: string,
): PrejoinJoinRequest {
  return {
    displayName,
    cameraDeviceId: cameraDeviceId === NO_DEVICE ? null : cameraDeviceId,
    micDeviceId: micDeviceId === NO_DEVICE ? null : micDeviceId,
  };
}

/** The shareable room URL. Built from origin + slug only; join tokens never appear in it. */
export function roomLink(origin: string, slug: string): string {
  return `${origin}/r/${slug}`;
}
